// Shared helpers for deciding whether a position reading is fresh enough
// to treat as "live". A speed value attached to a stale position shouldn't
// look like a live speedometer — so callers grey it out and append a
// relative age badge.

interface PositionLike {
  fixTime?: string;
  fix_time?: string;
}

const STALE_MS = 10 * 60 * 1000; // 10 min

/** True when the position's fix_time is older than `thresholdMs`.
 *  `position` may be undefined (no position record yet) or carry either
 *  `fixTime` or `fix_time` depending on which endpoint produced it. */
export function isPositionStale(
  position: PositionLike | null | undefined,
  thresholdMs: number = STALE_MS,
): boolean {
  if (!position) return true;
  const raw = position.fixTime || position.fix_time;
  if (!raw) return true;
  const fix = new Date(raw).getTime();
  if (!Number.isFinite(fix)) return true;
  return Date.now() - fix > thresholdMs;
}

interface DeviceLike {
  lastUpdate?: string | null;
}

/** ISO instant to show as a vehicle's "Updated" time: the last GPS fix,
 *  falling back to the device's last contact only when no position exists.
 *  Never max() the two — a tracker configured to send no records while
 *  parked keeps its link open, so Traccar bumps lastUpdate every minute
 *  while the last fix ages; max() then reads "1m" on a vehicle whose last
 *  position is an hour old, while the status classifier (fixTime-based)
 *  says pending. */
export function lastPositionUpdate(
  position: PositionLike | null | undefined,
  device: DeviceLike | null | undefined,
): string | null {
  const raw = position?.fixTime || position?.fix_time;
  if (raw) {
    const fix = new Date(raw).getTime();
    if (Number.isFinite(fix)) return new Date(fix).toISOString();
  }
  return device?.lastUpdate || null;
}

/** Compact "2h ago" style relative label for stale positions. Returns null
 *  when the position is fresh so call sites can conditionally render. */
export function staleAgeLabel(
  position: PositionLike | null | undefined,
  thresholdMs: number = STALE_MS,
): string | null {
  if (!isPositionStale(position, thresholdMs)) return null;
  const raw = position?.fixTime || position?.fix_time;
  if (!raw) return 'offline';
  const mins = Math.max(1, Math.floor((Date.now() - new Date(raw).getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
